import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUser as getCurrentUserFromAuth } from '@/utils/authUtils';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

export interface TimeEntry {
  id: string;
  user_id: string;
  project_id: string;
  start_time: string;
  end_time: string | null;
  duration_minutes: number | null;
  description: string | null;
  is_billable: boolean;
}

export interface ActiveSession {
  id: string;
  projectId: string;
  startTime: Date;
  description: string;
}

export interface TimeStore {
  // État
  activeSession: ActiveSession | null;
  timeEntries: TimeEntry[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setActiveSession: (session: ActiveSession | null) => void;
  setTimeEntries: (entries: TimeEntry[]) => void;
  addTimeEntry: (entry: TimeEntry) => void;
  updateTimeEntry: (id: string, updates: Partial<TimeEntry>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Actions asynchrones
  startTracking: (projectId: string, description: string) => Promise<void>;
  stopTracking: () => Promise<void>;
  switchProject: (projectId: string) => Promise<void>;
  fetchTimeEntries: (userId: string) => Promise<void>;
  createTimeEntry: (entry: Omit<TimeEntry, 'id'>) => Promise<TimeEntry>;
  updateTimeEntryAsync: (id: string, updates: Partial<TimeEntry>) => Promise<void>;
}

export const useTimeStore = create<TimeStore>()(
  persist(
    (set, get) => ({
      // État initial
      activeSession: null,
      timeEntries: [],
      isLoading: false,
      error: null,

      // Actions synchrones
      setActiveSession: (session) => set({ activeSession: session }),
      setTimeEntries: (entries) => set({ timeEntries: entries }),
      addTimeEntry: (entry) => set((state) => ({ 
        timeEntries: [entry, ...state.timeEntries] 
      })),
      updateTimeEntry: (id, updates) => set((state) => ({
        timeEntries: state.timeEntries.map(entry => 
          entry.id === id ? { ...entry, ...updates } : entry
        )
      })),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      // Actions asynchrones
      startTracking: async (projectId, description) => {
        try {
          set({ isLoading: true, error: null });
          
          const user = await getCurrentUserFromAuth();
          if (!user) throw new Error('Utilisateur non connecté');

          const now = new Date();
          let newEntry: TimeEntry;

          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const { data: entryId, error } = await supabase.rpc('pin_create_time_entry', {
              p_pin: pin,
              p_user_id: user.id,
              p_project_id: projectId,
              p_start_time: now.toISOString(),
              p_description: description || null,
              p_is_billable: true,
              p_status: 'active',
            });

            if (error) throw error;
            if (!entryId) throw new Error("Aucun ID d'entrée retourné");

            // La RPC retourne seulement l'ID; on reconstruit l'objet minimal localement.
            newEntry = {
              id: entryId as unknown as string,
              user_id: user.id,
              project_id: projectId,
              start_time: now.toISOString(),
              end_time: null,
              duration_minutes: null,
              description: description || null,
              is_billable: true,
            };
          } else {
            const { data, error } = await supabase
              .from('time_entries')
              .insert({
                user_id: user.id,
                project_id: projectId,
                start_time: now.toISOString(),
                description: description || null,
                is_billable: true,
                status: 'active',
              })
              .select()
              .single();

            if (error) throw error;
            if (!data) throw new Error('Aucune donnée retournée');
            newEntry = data as unknown as TimeEntry;
          }

          const activeSession: ActiveSession = {
            id: newEntry.id,
            projectId,
            startTime: now,
            description
          };

          set({ 
            activeSession,
            timeEntries: [newEntry, ...get().timeEntries]
          });

        } catch (error) {
          console.error('Erreur lors du démarrage:', error);
          set({ error: 'Impossible de démarrer le suivi' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      stopTracking: async () => {
        try {
          set({ isLoading: true, error: null });
          
          const { activeSession } = get();
          if (!activeSession) throw new Error('Aucune session active');

          const endTime = new Date();
          const startDate = typeof activeSession.startTime === 'string' ? new Date(activeSession.startTime) : activeSession.startTime;
          const durationMs = endTime.getTime() - startDate.getTime();
          const durationMinutes = Math.ceil(durationMs / (1000 * 60));

          let updatedEntry: TimeEntry;

          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const { error } = await supabase.rpc('pin_update_time_entry', {
              p_pin: pin,
              p_entry_id: activeSession.id,
              p_end_time: endTime.toISOString(),
              p_duration_minutes: durationMinutes,
              p_status: 'closed',
            });
            if (error) throw error;

            const currentEntry = get().timeEntries.find((e) => e.id === activeSession.id);
            updatedEntry = {
              ...(currentEntry || {
                id: activeSession.id,
                user_id: '',
                project_id: activeSession.projectId,
                start_time: startDate.toISOString(),
                end_time: null,
                duration_minutes: null,
                description: activeSession.description || null,
                is_billable: true,
              }),
              end_time: endTime.toISOString(),
              duration_minutes: durationMinutes,
            };
          } else {
            const { data, error } = await supabase
              .from('time_entries')
              .update({
                end_time: endTime.toISOString(),
                duration_minutes: durationMinutes,
                status: 'closed',
                updated_at: new Date().toISOString(),
              })
              .eq('id', activeSession.id)
              .select()
              .single();

            if (error) throw error;
            if (!data) throw new Error('Aucune donnée retournée');
            updatedEntry = data as unknown as TimeEntry;
          }

          set((state) => ({
            activeSession: null,
            timeEntries: state.timeEntries.map(entry => 
              entry.id === activeSession.id ? updatedEntry : entry
            )
          }));

        } catch (error) {
          console.error('Erreur lors de l\'arrêt:', error);
          set({ error: 'Impossible d\'arrêter le suivi' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      fetchTimeEntries: async (userId) => {
        try {
          set({ isLoading: true, error: null });
          
          let data: any[] | null = null;
          let error: any = null;

          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const res = await supabase.rpc('pin_list_user_time_entries', {
              p_pin: pin,
              p_user_id: userId,
              p_start: null,
              p_end: null,
              p_limit: 1000,
              p_offset: 0,
            });
            data = (res.data as any[]) || [];
            error = res.error;
          } else {
            const res = await supabase
              .from('time_entries')
              .select('*')
              .eq('user_id', userId)
              .order('start_time', { ascending: false });
            data = (res.data as any[]) || [];
            error = res.error;
          }

          if (error) throw error;
          
          set({ timeEntries: (data || []) as TimeEntry[] });

        } catch (error) {
          console.error('Erreur lors du chargement:', error);
          set({ error: 'Impossible de charger les entrées' });
        } finally {
          set({ isLoading: false });
        }
      },

      createTimeEntry: async (entry) => {
        try {
          set({ isLoading: true, error: null });
          
          let data: any = null;
          let error: any = null;

          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const currentUser = await getCurrentUserFromAuth();
            if (!currentUser) throw new Error('Utilisateur non connecté');

            // Cas 1: création pour soi-même
            if (entry.user_id === currentUser.id) {
              const { data: entryId, error: createErr } = await supabase.rpc('pin_create_time_entry', {
                p_pin: pin,
                p_user_id: entry.user_id,
                p_project_id: entry.project_id,
                p_start_time: entry.start_time,
                p_description: entry.description || null,
                p_is_billable: entry.is_billable ?? true,
                p_status: entry.end_time ? 'closed' : 'active',
              });
              if (createErr) throw createErr;
              if (!entryId) throw new Error("Aucun ID d'entrée retourné");

              // Si l'entrée est "fermée", on applique l'end_time/duration via update RPC
              if (entry.end_time) {
                const { error: updErr } = await supabase.rpc('pin_update_time_entry', {
                  p_pin: pin,
                  p_entry_id: entryId,
                  p_end_time: entry.end_time,
                  p_duration_minutes: entry.duration_minutes ?? null,
                  p_status: 'closed',
                  p_description: entry.description ?? null,
                  p_is_billable: entry.is_billable ?? null,
                });
                if (updErr) throw updErr;
              }

              data = { ...entry, id: entryId };
            } else {
              // Cas 2: création pour un employé (manager)
              const { data: entryId, error: createErr } = await supabase.rpc('pin_create_time_entry_for_employee', {
                p_pin: pin,
                p_user_id: entry.user_id,
                p_project_id: entry.project_id,
                p_start_time: entry.start_time,
                p_end_time: entry.end_time ?? null,
                p_duration_minutes: entry.duration_minutes ?? null,
                p_description: entry.description ?? null,
                p_is_billable: entry.is_billable ?? true,
                p_hourly_rate: null,
                p_status: entry.end_time ? 'closed' : 'active',
              });
              if (createErr) throw createErr;
              if (!entryId) throw new Error("Aucun ID d'entrée retourné");
              data = { ...entry, id: entryId };
            }
          } else {
            const res = await supabase
              .from('time_entries')
              .insert([entry])
              .select()
              .single();
            data = res.data;
            error = res.error;
          }

          if (error) throw error;
          
          set((state) => ({ 
            timeEntries: [data as TimeEntry, ...state.timeEntries] 
          }));
          
          return data as TimeEntry;
        } catch (error) {
          console.error('Erreur lors de la création:', error);
          set({ error: 'Impossible de créer l\'entrée' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateTimeEntryAsync: async (id, updates) => {
        try {
          set({ isLoading: true, error: null });
  
          let updatedEntry: any = null;

          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const { error } = await supabase.rpc('pin_update_time_entry', {
              p_pin: pin,
              p_entry_id: id,
              p_end_time: (updates as any).end_time ?? null,
              p_duration_minutes: (updates as any).duration_minutes ?? null,
              p_description: (updates as any).description ?? null,
              p_is_billable: (updates as any).is_billable ?? null,
              p_status: (updates as any).status ?? null,
              p_project_id: (updates as any).project_id ?? null,
            });
            if (error) throw error;

            const currentEntry = get().timeEntries.find((e) => e.id === id);
            updatedEntry = {
              ...(currentEntry || { id }),
              ...updates,
              updated_at: new Date().toISOString(),
            };
          } else {
            const { data, error } = await supabase
              .from('time_entries')
              .update({ ...updates, updated_at: new Date().toISOString() })
              .eq('id', id)
              .select()
              .single();
            if (error) throw error;
            updatedEntry = data;
          }
  
          // Mise à jour locale du store
          set((state) => ({
            timeEntries: state.timeEntries.map((entry) =>
              entry.id === id ? { ...entry, ...(updatedEntry as Partial<TimeEntry>) } : entry
            ),
          }));
        } catch (error) {
          console.error('Erreur lors de la mise à jour:', error);
          set({ error: 'Impossible de mettre à jour l\'entrée' });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      switchProject: async (newProjectId) => {
        try {
          const { activeSession } = get();
          if (!activeSession) throw new Error('Aucune session active');

          // Mettre à jour la BD
          if (shouldUsePinMode()) {
            const pin = getUserPinOrThrow();
            const { error } = await supabase.rpc('pin_update_time_entry', {
              p_pin: pin,
              p_entry_id: activeSession.id,
              p_project_id: newProjectId,
            });
            if (error) throw error;
          } else {
            const { error } = await supabase
              .from('time_entries')
              .update({ project_id: newProjectId })
              .eq('id', activeSession.id);
            if (error) throw error;
          }

          // Mettre à jour le state local
          set({ activeSession: { ...activeSession, projectId: newProjectId } });
        } catch (error) {
          console.error('Erreur changement projet:', error);
          set({ error: 'Impossible de changer de projet' });
          throw error;
        }

      }
    }),
    {
      name: 'time-tracker-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        activeSession: state.activeSession,
        timeEntries: state.timeEntries.slice(0, 50) // Limiter la persistance
      })
    }
  )
); 